import AnnotationOverview from "../component/AnnotationOverview";
import CategoryPropertyMatrix from "../component/CategoryPropertyMatrix";
import React, { useEffect, useMemo, useState } from "react";
import { Bot, ChevronDown, ChevronRight, GripVertical, RefreshCw, Search, Undo2 } from "lucide-react";

import { automapCatalogTaxonomy, catalogDictionary, catalogTaxonomy, saveCatalogTaxonomyMapping } from "../api/catalogSearchApi";
import styles from "./CatalogTaxonomyPage.module.css";

const sourceLabel = value => ({ digikey: "DigiKey", mouser: "Mouser", chipdip: "Чип и Дип", symmetron: "Симметрон" })[value] || value;

const filterTree = (nodes, term) => {
    if (!term.trim()) return nodes;
    const query = term.trim().toLowerCase();
    return (nodes || []).flatMap(node => {
        const children = filterTree(node.children || [], term);
        return `${node.label} ${node.code}`.toLowerCase().includes(query) || children.length ? [{ ...node, children, hasGroupChildren: Boolean(node.children?.length) }] : [];
    });
};

function TaxonomyNode({ node, depth, open, toggle, onDrop, busy, propLookup, onShowProps }) {
    const hasChildren = Boolean(node.children?.length);
    const expanded = open.has(node.id);
    const properties = propLookup ? propLookup(node.label) : [];
    return <div className={styles.node}>
        <div className={styles.nodeRow} style={{ "--depth": depth }}
            onDragOver={event => event.preventDefault()}
            onDrop={event => { event.preventDefault(); onDrop(node.id); }}>
            {hasChildren ? <button type="button" className={styles.disclosure} aria-label={`${expanded ? "Свернуть" : "Развернуть"} ${node.label}`} onClick={() => toggle(node.id)}>
                {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button> : <span className={styles.leaf} />}
            <span className={styles.nodeLabel}>{node.label}</span>
            <small>{node.count}</small>
            {(node.hasGroupChildren ?? hasChildren) && <button type="button" className={styles.propsButton} onClick={() => onShowProps(node, properties)}>Характеристики</button>}
            <span className={styles.dropHint}>{busy ? "Сохранение…" : "Перетащите сюда"}</span>
        </div>
        {hasChildren && expanded && node.children.map(child => <TaxonomyNode key={child.id} node={child} depth={depth + 1} open={open} toggle={toggle} onDrop={onDrop} busy={busy} propLookup={propLookup} onShowProps={onShowProps} />)}
    </div>;
}

export default function CatalogTaxonomyPage() {
    const [showAnnotationOverview,setShowAnnotationOverview]=useState(false);
    const [data, setData] = useState({ tree: [], unmapped: [], mappings: [], stats: {} });
    const [dictionary, setDictionary] = useState({});
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [filter, setFilter] = useState("");
    const [open, setOpen] = useState(new Set());
    const [dragged, setDragged] = useState(null);
    const [propsModal, setPropsModal] = useState(null);

    const load = async () => {
        setLoading(true); setError("");
        try {
            const [next, dict] = await Promise.all([catalogTaxonomy(), catalogDictionary()]);
            setData(next);
            setDictionary(dict);
            setOpen(current => current.size ? current : new Set((next.tree || []).map(node => node.id)));
        } catch (loadError) { setError(loadError.message || "Не удалось загрузить категории"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const visibleTree = useMemo(() => filterTree(data.tree, filter), [data.tree, filter]);
    const propLookup = useMemo(() => {
        const categoryParameters = dictionary.category_parameters || {};
        const categoryLabels = dictionary.category_labels || {};
        const paramLabels = Object.fromEntries((dictionary.parameters || []).map(p => [p.code, p]));
        const englishByRussian = Object.fromEntries(Object.entries(categoryLabels).map(([en, ru]) => [ru, en]));
        return label => {
            const en = englishByRussian[label] || label;
            const lower = String(en).toLowerCase();
            for (const [cat, codes] of Object.entries(categoryParameters)) {
                if (cat.toLowerCase() === lower) {
                    return codes.map(code => { const p = paramLabels[code]; return `${p?.ru || p?.name || code}${p?.unit ? ` · ${p.unit}` : ''}`; });
                }
            }
            return [];
        };
    }, [dictionary]);
    const findNode = (id, nodes=data.tree) => {for(const node of nodes||[]){if(node.id===id)return node;const found=findNode(id,node.children||[]);if(found)return found;}return null;};
    const showProperties = node => setPropsModal(findNode(node.id)||node);
    const toggle = id => setOpen(current => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; });
    const applyMapping = async nodeId => {
        if (!dragged || busy) return;
        setBusy(true); setError(""); setNotice("");
        try {
            const next = await saveCatalogTaxonomyMapping({ supplier: dragged.supplier, source_path: dragged.source_path, node_id: nodeId });
            setData(next); setDragged(null); setNotice(`Категория «${dragged.source_category}» сопоставлена вручную.`);
        } catch (saveError) { setError(saveError.message || "Не удалось сохранить соответствие"); }
        finally { setBusy(false); }
    };
    const automap = async () => {
        setBusy(true); setError(""); setNotice("");
        try {
            const next = await automapCatalogTaxonomy(20);
            setData(next); setNotice(`DeepSeek сопоставил категорий: ${next.mapped || 0}. Осталось: ${next.remaining || 0}.`);
        } catch (mapError) { setError(mapError.message || "DeepSeek не смог выполнить сопоставление"); }
        finally { setBusy(false); }
    };
    const undo = async mapping => {
        setBusy(true); setError("");
        try {
            const next = await saveCatalogTaxonomyMapping({ supplier: mapping.supplier, source_path: mapping.source_path, node_id: null });
            setData(next); setNotice(`Соответствие «${mapping.source_category}» отменено.`);
        } catch (undoError) { setError(undoError.message || "Не удалось отменить соответствие"); }
        finally { setBusy(false); }
    };

    return <div className={styles.page}>
        <header className={styles.header}>
            <div><h2>Единая иерархия категорий</h2><p>Новые категории автоматически сопоставляются через DeepSeek после импорта. Ручные правила имеют приоритет. Перетащите категорию поставщика на нужный раздел, чтобы задать ручное правило.</p></div>
            <div className={styles.actions}>
                <button className={styles.secondary} type="button" onClick={load} disabled={loading || busy}><RefreshCw size={16} />Обновить</button>
                <button className={styles.primary} type="button" onClick={automap} disabled={loading || busy || !data.unmapped?.length}><Bot size={17} />{busy ? "Обработка…" : "Сопоставить через DeepSeek"}</button>
            </div>
        </header>
        <div className={styles.stats}>
            <span><strong>{data.stats?.nodes || 0}</strong> узлов дерева</span>
            <span><strong>{data.stats?.mapped || 0}</strong> сопоставлено</span>
            <span className={data.stats?.unmapped ? styles.warning : ""}><strong>{data.stats?.unmapped || 0}</strong> ожидают</span>
        </div>
        {notice && <p className={styles.notice}>{notice}</p>}
        {error && <p className={styles.error} role="alert">{error}</p>}
        <button type="button" className={styles.secondary} onClick={()=>setShowAnnotationOverview(v=>!v)}>Разметка всех категорий</button>
        {showAnnotationOverview&&<AnnotationOverview onSelect={showProperties}/>}
        <div className={styles.workspace}>
            <section className={styles.treePanel}>
                <div className={styles.panelHeader}><div><strong>Иерархия</strong><span>Целевой каталог</span></div><label><Search size={15} /><input value={filter} onChange={event => setFilter(event.target.value)} placeholder="Найти раздел" /></label></div>
                <div className={styles.tree}>{loading ? <p>Загрузка…</p> : visibleTree.map(node => <TaxonomyNode key={node.id} node={node} depth={0} open={open} toggle={toggle} onDrop={applyMapping} busy={busy} propLookup={propLookup} onShowProps={showProperties} />)}</div>
            </section>
            <section className={styles.queuePanel}>
                <div className={styles.panelHeader}><div><strong>Не сопоставлено</strong><span>Перетащите строку в дерево</span></div><span className={styles.badge}>{data.unmapped?.length || 0}</span></div>
                <div className={styles.queue}>{data.unmapped?.map(item => <article key={item.source_key} className={`${styles.sourceCard} ${dragged?.source_key === item.source_key ? styles.dragging : ""}`} draggable={!busy}
                    onDragStart={event => { setDragged(item); event.dataTransfer?.setData("text/plain", item.source_key); }} onDragEnd={() => setDragged(null)}>
                    <GripVertical size={17} /><div><strong>{item.source_category}</strong><span>{sourceLabel(item.supplier)} · позиций: {item.products}</span><small>{item.source_path.join(" → ")}</small></div>
                </article>)}</div>
            </section>
        </div>
        <section className={styles.mappedPanel}>
            <div className={styles.panelHeader}><div><strong>Последние соответствия</strong><span>Перетащите соответствие в дерево, чтобы изменить вручную. Отмена возвращает его в автоматическую очередь.</span></div></div>
            <div className={styles.mappingTable}>
                <div className={styles.mappingHead}><span>Категория поставщика</span><span>Раздел каталога</span><span>Способ</span><span>Уверенность</span><span /></div>
                {data.mappings?.map(mapping => <div className={styles.mappingRow} key={mapping.source_key} draggable={!busy} onDragStart={event => { setDragged(mapping); event.dataTransfer?.setData("text/plain", mapping.source_key); }} onDragEnd={() => setDragged(null)}>
                    <span><strong>{mapping.source_category}</strong><small>{sourceLabel(mapping.supplier)}</small></span>
                    <span>{mapping.taxonomy_label}</span><span>{mapping.method === "manual" ? "Сотрудник" : "DeepSeek"}</span>
                    <span>{mapping.confidence == null ? "—" : `${Math.round(Number(mapping.confidence) * 100)}%`}</span>
                    <button type="button" aria-label={`Отменить ${mapping.source_category}`} onClick={() => undo(mapping)} disabled={busy}><Undo2 size={15} />Отменить</button>
                </div>)}
            </div>
        </section>
        {propsModal && <div className={styles.modalOverlay} onClick={() => setPropsModal(null)}>
            <div className={styles.modal} onClick={event => event.stopPropagation()} role="dialog" aria-modal="true">
                <div className={styles.modalHead}><h3>Характеристики — {propsModal.label}</h3><button type="button" className={styles.modalClose} onClick={() => setPropsModal(null)} aria-label="Закрыть">✕</button></div>
                <CategoryPropertyMatrix key={propsModal.id} node={propsModal} definitions={dictionary.parameters || []} />
            </div>
        </div>}
    </div>;
}
